import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy, ArrowRight, CheckCircle2, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SUPPORT_EMAIL = "support@ruleai.systems";

export function SupportButton({ variant = "default", className = "" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", priority: "normal", category: "general" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.subject.trim().length < 4) { toast.error("Subject required"); return; }
    if (form.message.trim().length < 10) { toast.error("Message too short"); return; }
    setLoading(true);
    try {
      await api.post("/support/tickets", form);
      setDone(true);
      toast.success("Ticket created — we'll reply within 24 hours");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create ticket"); }
    finally { setLoading(false); }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => { setDone(false); setForm({ subject: "", message: "", priority: "normal", category: "general" }); }, 300);
  };

  const Btn = variant === "ghost" ? (
    <button onClick={() => setOpen(true)} className={`inline-flex items-center gap-2 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors ${className}`} data-testid="support-btn-ghost">
      <LifeBuoy className="w-3.5 h-3.5" /> Support
    </button>
  ) : (
    <Button onClick={() => setOpen(true)} variant="outline" size="sm" className={`rounded-sm ${className}`} data-testid="support-btn">
      <LifeBuoy className="w-3.5 h-3.5 mr-2" /> Support
    </Button>
  );

  return (
    <>
      {Btn}
      <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else setOpen(true); }}>
        <DialogContent className="sm:max-w-lg rounded-sm" data-testid="support-dialog">
          {!done ? (
            <>
              <DialogHeader>
                <div className="overline text-accent">◉ SUPPORT · 24-HR REPLY</div>
                <DialogTitle className="font-display text-2xl font-bold tracking-tight">How can we help?</DialogTitle>
                <p className="text-sm text-muted-foreground pt-1">
                  Send a ticket and we&rsquo;ll reply personally. For faster answers, email {" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">{SUPPORT_EMAIL}</a>.
                </p>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Category</label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="support-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General question</SelectItem>
                        <SelectItem value="bug">Bug report</SelectItem>
                        <SelectItem value="billing">Billing</SelectItem>
                        <SelectItem value="feature_request">Feature request</SelectItem>
                        <SelectItem value="integration">Integration help</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="overline text-muted-foreground block mb-1.5">Priority</label>
                    <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="support-priority"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent — production down</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="overline text-muted-foreground block mb-1.5">Subject *</label>
                  <Input
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="One-line summary"
                    className="rounded-sm"
                    maxLength={200}
                    data-testid="support-subject"
                  />
                </div>
                <div>
                  <label className="overline text-muted-foreground block mb-1.5">Details *</label>
                  <Textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="What's happening? What did you expect? Screenshots or steps to reproduce are great."
                    className="rounded-sm resize-none"
                    maxLength={5000}
                    data-testid="support-message"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="support-submit-btn">
                  {loading ? "Sending…" : (<>Send ticket <ArrowRight className="w-4 h-4 ml-2" /></>)}
                </Button>
              </form>
            </>
          ) : (
            <div className="py-8 text-center" data-testid="support-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <div className="font-display font-bold text-2xl mt-4">Ticket created.</div>
              <p className="text-sm text-muted-foreground mt-3 max-w-sm mx-auto">
                We&rsquo;ll reply within 24 hours. Track its status any time in <strong className="text-foreground">Support &rarr; My tickets</strong> (coming soon).
              </p>
              <div className="mt-4 pt-4 border-t border-border">
                <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-2 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-foreground">
                  <Mail className="w-3.5 h-3.5" /> Or email {SUPPORT_EMAIL}
                </a>
              </div>
              <Button onClick={close} className="mt-6 rounded-sm bg-primary hover:bg-primary/90" data-testid="support-close-btn">Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
